import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  Search,
  ChevronRight,
  Briefcase,
  GraduationCap,
  ExternalLink,
  FileText,
  Filter,
  Edit2,
  Trash2,
  Loader2,
  Link,
  Copy,
  Check,
  RefreshCw,
  Square,
  Calendar,
  CheckCircle,
  AlertCircle,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Students() {
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [programFilter, setProgramFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const limit = 100;

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingStudent, setEditingStudent] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [studentToDelete, setStudentToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [copiedId, setCopiedId] = useState(null);
  const [generatingLink, setGeneratingLink] = useState(null);
  const [regenerating, setRegenerating] = useState(false);
  const [regenProgress, setRegenProgress] = useState(null);
  const [selectedStudents, setSelectedStudents] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [sortField, setSortField] = useState("");
  const [sortDir, setSortDir] = useState("asc");

  useEffect(() => {
    fetchStudents();
  }, [page, search, programFilter, dateFrom, dateTo]);

  const fetchStudents = async () => {
    try {
      setLoading(true);
      const params = { skip: page * limit, limit, search };
      if (programFilter !== "all") params.program_type = programFilter;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const response = await axios.get(`${API}/students`, { params });
      setStudents(response.data.students);
      setTotal(response.data.total);
    } catch (error) {
      toast.error("Failed to fetch students");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearch(e.target.value);
    setPage(0);
  };

  const viewReport = (studentId) => navigate(`/students/${studentId}`);

  const getProgramType = (student) => {
    if (student.program_type === "PGP_TBM" || student.program_type === "PGP_YLC") return student.program_type;
    return (student.total_work_experience || 0) < 2 ? "PGP_YLC" : "PGP_TBM";
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    try {
      const d = new Date(dateStr);
      return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
    } catch { return "-"; }
  };

  const openEditModal = (student) => {
    setEditingStudent(student);
    setEditForm({
      name: student.name || "",
      email: student.email || "",
      mobile: student.mobile || "",
      linkedin: student.linkedin || "",
      current_organization: student.current_organization || "",
      current_designation: student.current_designation || "",
      total_work_experience: student.total_work_experience || 0,
      program_type: student.program_type || getProgramType(student),
      resume_link: student.resume_link || "",
    });
    setEditModalOpen(true);
  };

  const handleEditFormChange = (field, value) => setEditForm((prev) => ({ ...prev, [field]: value }));

  const handleSaveEdit = async () => {
    if (!editingStudent) return;
    setSaving(true);
    try {
      await axios.put(`${API}/students/${editingStudent.id}`, editForm);
      toast.success("Student updated successfully");
      setEditModalOpen(false);
      setEditingStudent(null);
      fetchStudents();
    } catch { toast.error("Failed to update student"); }
    finally { setSaving(false); }
  };

  const openDeleteDialog = (student) => { setStudentToDelete(student); setDeleteDialogOpen(true); };

  const handleDelete = async () => {
    if (!studentToDelete) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/students/${studentToDelete.id}`);
      toast.success("Student deleted successfully");
      setDeleteDialogOpen(false);
      setStudentToDelete(null);
      fetchStudents();
    } catch { toast.error("Failed to delete student"); }
    finally { setDeleting(false); }
  };

  const handleShareLink = async (student) => {
    setGeneratingLink(student.id);
    try {
      const response = await axios.get(`${API}/share/generate/${student.id}`);
      const shareUrl = `${window.location.origin}${response.data.share_url}`;
      try {
        await navigator.clipboard.writeText(shareUrl);
        setCopiedId(student.id);
        toast.success(`Share link copied for ${student.name}!`);
      } catch {
        const textArea = document.createElement("textarea");
        textArea.value = shareUrl;
        textArea.style.position = "fixed";
        textArea.style.left = "-9999px";
        document.body.appendChild(textArea);
        textArea.select();
        try { document.execCommand("copy"); setCopiedId(student.id); toast.success(`Share link copied!`); }
        catch { toast.info(`Share link:`, { description: shareUrl, duration: 10000 }); }
        document.body.removeChild(textArea);
      }
      setTimeout(() => setCopiedId(null), 3000);
    } catch (error) {
      if (error.response?.status === 404) toast.error("Report not generated yet");
      else toast.error("Failed to generate share link");
    } finally { setGeneratingLink(null); }
  };

  const totalPages = Math.ceil(total / limit);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(prev => prev === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDir("asc");
    }
  };

  const sortedStudents = React.useMemo(() => {
    if (!sortField) return students;
    return [...students].sort((a, b) => {
      let aVal, bVal;
      if (sortField === "name") { aVal = a.name || ""; bVal = b.name || ""; }
      else if (sortField === "organization") { aVal = a.current_organization || ""; bVal = b.current_organization || ""; }
      else if (sortField === "program") { aVal = a.program_type || ""; bVal = b.program_type || ""; }
      else if (sortField === "date") {
        aVal = a.created_at ? new Date(a.created_at).getTime() : 0;
        bVal = b.created_at ? new Date(b.created_at).getTime() : 0;
        return sortDir === "asc" ? aVal - bVal : bVal - aVal;
      }
      else if (sortField === "status") { aVal = a.report_status || ""; bVal = b.report_status || ""; }
      else return 0;
      const cmp = String(aVal).localeCompare(String(bVal));
      return sortDir === "asc" ? cmp : -cmp;
    });
  }, [students, sortField, sortDir]);

  const SortHeader = ({ field, children }) => (
    <TableHead className="text-gray-500 cursor-pointer select-none hover:text-gray-700" onClick={() => handleSort(field)}>
      <div className="flex items-center gap-1">
        {children}
        {sortField === field && <span className="text-xs">{sortDir === "asc" ? "▲" : "▼"}</span>}
      </div>
    </TableHead>
  );

  const handleExportCSV = async () => {
    try {
      const params = {};
      if (selectedStudents.size > 0) {
        params.student_ids = Array.from(selectedStudents).join(",");
      } else {
        if (search) params.search = search;
        if (programFilter !== "all") params.program_type = programFilter;
        if (dateFrom) params.date_from = dateFrom;
        if (dateTo) params.date_to = dateTo;
      }
      const response = await axios.get(`${API}/data/students/export`, { params, responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `student_directory_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      const label = selectedStudents.size > 0 ? `${selectedStudents.size} selected students` : "filtered students";
      toast.success(`CSV exported (${label})`);
    } catch { toast.error('Failed to export CSV'); }
  };

  const toggleSelectStudent = (id) => {
    setSelectedStudents(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedStudents.size === students.length) setSelectedStudents(new Set());
    else setSelectedStudents(new Set(students.map(s => s.id)));
  };

  const handleBulkDelete = async () => {
    setBulkDeleting(true);
    try {
      const ids = Array.from(selectedStudents);
      await axios.post(`${API}/students/bulk-delete`, { student_ids: ids });
      toast.success(`${ids.length} students deleted`);
      setSelectedStudents(new Set());
      setBulkDeleteDialogOpen(false);
      fetchStudents();
    } catch { toast.error("Failed to delete students"); }
    setBulkDeleting(false);
  };

  // --- Regeneration handlers ---
  const startRegenPoll = () => {
    const pollInterval = setInterval(async () => {
      try {
        const statusRes = await axios.get(`${API}/students/regenerate-status`);
        const s = statusRes.data;
        setRegenProgress(s);
        if (s.status === "completed" || s.status === "idle" || s.status === "stopped" || s.status === "error") {
          clearInterval(pollInterval);
          setRegenerating(false);
          setRegenProgress(null);
          if (s.status === "stopped") toast.info(`Stopped. ${s.reports_generated} reports done.`);
          else if (s.total_students === 0) toast.success("All reports already complete!");
          else if (s.reports_generated > 0) toast.success(`${s.reports_generated} reports generated!`);
          if (s.placeholders > 0) {
            toast.error(`${s.placeholders} report(s) failed and are marked Incomplete. First error: ${(s.errors || [])[0] || "see server log"}`, { duration: 15000 });
          } else if (s.status === "error") {
            toast.error(`Generation stopped with an error: ${(s.errors || [])[0] || "see server log"}`, { duration: 15000 });
          }
          fetchStudents();
        }
      } catch { /* ignore */ }
    }, 3000);
  };

  const handleRegenerateAll = async () => {
    setRegenerating(true); setRegenProgress(null);
    try {
      const res = await axios.post(`${API}/students/regenerate-all-reports`, {}, { timeout: 300000 });
      if (res.data.status === "started") { toast.success("Regeneration started..."); startRegenPoll(); }
      else if (res.data.status === "already_running") { toast.info(`Already running`); setRegenerating(false); }
      else { setRegenerating(false); fetchStudents(); }
    } catch { toast.error("Failed"); setRegenerating(false); }
  };

  const handleRegenerateIncomplete = async () => {
    setRegenerating(true); setRegenProgress(null);
    try {
      const res = await axios.post(`${API}/students/regenerate-incomplete-reports`, {}, { timeout: 300000 });
      if (res.data.status === "started") { toast.info("Regenerating incomplete..."); startRegenPoll(); }
      else if (res.data.status === "already_running") { toast.info(`Already running`); setRegenerating(false); }
    } catch { toast.error("Failed"); setRegenerating(false); }
  };

  const handleRegenerateSelected = async () => {
    if (selectedStudents.size === 0) return;
    setRegenerating(true); setRegenProgress(null);
    try {
      const ids = Array.from(selectedStudents);
      const res = await axios.post(`${API}/students/regenerate-selected`, { student_ids: ids }, { timeout: 300000 });
      if (res.data.status === "started") { toast.info(`Regenerating ${ids.length} selected...`); startRegenPoll(); }
      else if (res.data.status === "already_running") { toast.info(`Already running`); setRegenerating(false); }
    } catch { toast.error("Failed"); setRegenerating(false); }
  };

  // Rebuild course/prep/mentor sections from the latest uploads. No AI, free, takes seconds.
  const handleRefreshReports = async () => {
    try {
      toast.info("Refreshing roadmaps from the uploaded data...");
      const res = await axios.post(`${API}/students/refresh-reports`, {}, { timeout: 300000 });
      toast.success(res.data.message);
      (res.data.warnings || []).forEach((w) => toast.warning(w));
      fetchStudents();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to refresh reports"); }
  };

  // Fill empty prep-roadmap cells for every domain with Claude-written suggestions (existing content is kept)
  const handleFillPrepGaps = async () => {
    if (!window.confirm("Fill the empty cells of the Prep Roadmap for every domain with Claude-written suggestions? Existing content is kept. Takes 2-4 minutes.")) return;
    try {
      toast.info("Filling prep roadmap gaps... this takes a few minutes.");
      const res = await axios.post(`${API}/counselling/fill-gaps`, {}, { timeout: 600000 });
      toast.success(res.data.message, { duration: 10000 });
      fetchStudents();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to fill prep gaps"); }
  };

  const handleStopRegeneration = async () => {
    try { await axios.post(`${API}/students/stop-regeneration`); toast.info("Stopping..."); }
    catch { toast.error("Failed to stop"); }
  };

  const handleRecalcWorkExp = async () => {
    try {
      toast.info("Recalculating work experience...");
      const res = await axios.post(`${API}/students/recalculate-work-experience`);
      const { updated_count, total_students } = res.data;
      toast.success(`Updated ${updated_count} of ${total_students} students`);
      fetchStudents();
    } catch { toast.error("Failed to recalculate"); }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="font-manrope text-3xl font-bold text-gray-900">Student Directory</h1>
          <p className="text-gray-500 mt-1">{total} students in the system</p>
        </div>
        <div className="flex items-center gap-3">
          {selectedStudents.size > 0 && !regenerating && (
            <>
              <Button
                onClick={handleRegenerateSelected}
                variant="outline"
                className="border-[#E8B923] text-[#B8941C] hover:bg-[#FFF8E1]"
                data-testid="regenerate-selected-btn"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Regenerate ({selectedStudents.size})
              </Button>
              <Button
                onClick={() => setBulkDeleteDialogOpen(true)}
                variant="outline"
                className="border-red-200 text-red-600 hover:bg-red-50"
                data-testid="bulk-delete-btn"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete ({selectedStudents.size})
              </Button>
            </>
          )}
          {regenerating && (
            <div className="flex items-center gap-2">
              <div className="text-sm text-gray-500 font-medium">
                {regenProgress
                  ? `${regenProgress.processed ?? regenProgress.reports_generated}/${regenProgress.total_students} done` +
                    `${regenProgress.placeholders ? `, ${regenProgress.placeholders} failed` : ''}` +
                    `${regenProgress.eta_minutes ? ` (~${regenProgress.eta_minutes} min left)` : ''}`
                  : "Starting..."}
              </div>
              <Button onClick={handleStopRegeneration} className="bg-red-500 text-white hover:bg-red-600" data-testid="stop-regeneration-btn">
                <Square className="h-4 w-4 mr-2" /> Stop
              </Button>
            </div>
          )}
          <Button onClick={handleExportCSV} variant="outline" className="border-gray-200 text-gray-600 hover:bg-gray-50" data-testid="export-csv-btn">
            <FileText className="h-4 w-4 mr-2" /> Export CSV
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="bg-white border border-gray-200/80" style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search by name, email, or application number..."
                value={search}
                onChange={handleSearch}
                className="pl-10 bg-white border-gray-200 text-gray-900 placeholder:text-gray-400 focus:border-[#E8B923]"
                data-testid="student-search-input"
              />
            </div>
            <Select value={programFilter} onValueChange={(value) => { setProgramFilter(value); setPage(0); }}>
              <SelectTrigger className="w-full md:w-[180px] bg-white border-gray-200 text-gray-700">
                <Filter className="h-4 w-4 mr-2 text-gray-400" />
                <SelectValue placeholder="Program Type" />
              </SelectTrigger>
              <SelectContent className="bg-white border-gray-200">
                <SelectItem value="all">All Programs</SelectItem>
                <SelectItem value="PGP_TBM">PGP TBM</SelectItem>
                <SelectItem value="PGP_YLC">PGP YLC</SelectItem>
              </SelectContent>
            </Select>
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-gray-400 hidden md:block" />
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => { setDateFrom(e.target.value); setPage(0); }}
                className="w-[150px] bg-white border-gray-200 text-gray-700 text-sm"
                data-testid="date-from-filter"
              />
              <span className="text-gray-400 text-sm">to</span>
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => { setDateTo(e.target.value); setPage(0); }}
                className="w-[150px] bg-white border-gray-200 text-gray-700 text-sm"
                data-testid="date-to-filter"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Pagination - at top */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-500">
            Showing {page * limit + 1} - {Math.min((page + 1) * limit, total)} of {total}
          </p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" className="border-gray-200 text-gray-600 hover:bg-gray-50" onClick={() => setPage(Math.max(0, page - 1))} disabled={page === 0} data-testid="prev-page-btn">Previous</Button>
            <Button variant="outline" size="sm" className="border-gray-200 text-gray-600 hover:bg-gray-50" onClick={() => setPage(Math.min(totalPages - 1, page + 1))} disabled={page >= totalPages - 1} data-testid="next-page-btn">Next</Button>
          </div>
        </div>
      )}

      {/* Students Table */}
      <Card className="bg-white border border-gray-200/80" style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64"><div className="spinner" /></div>
          ) : students.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-gray-400">
              <GraduationCap className="h-12 w-12 mb-3 opacity-50" />
              <p>No students found</p>
              <p className="text-sm mt-1">Upload student data to get started</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-gray-200 hover:bg-transparent">
                    <TableHead className="w-10">
                      <input type="checkbox" checked={students.length > 0 && selectedStudents.size === students.length} onChange={toggleSelectAll} className="rounded border-gray-300 cursor-pointer" data-testid="select-all-checkbox" />
                    </TableHead>
                    <SortHeader field="program">Program</SortHeader>
                    <SortHeader field="name">Name / Email</SortHeader>
                    <SortHeader field="organization">Organization</SortHeader>
                    <SortHeader field="date">Date Added</SortHeader>
                    <SortHeader field="status">Status</SortHeader>
                    <TableHead className="text-gray-500">Resume</TableHead>
                    <TableHead className="text-gray-500">Share Link</TableHead>
                    <TableHead className="text-gray-500 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedStudents.map((student) => {
                    const programType = getProgramType(student);
                    const isComplete = student.report_status === "Complete";
                    return (
                      <TableRow key={student.id} className="border-gray-100 hover:bg-gray-50" data-testid={`student-row-${student.id}`}>
                        <TableCell>
                          <input type="checkbox" checked={selectedStudents.has(student.id)} onChange={() => toggleSelectStudent(student.id)} className="rounded border-gray-300 cursor-pointer" data-testid={`select-student-${student.id}`} />
                        </TableCell>
                        <TableCell>
                          <Badge className={programType === "PGP_TBM" ? "bg-blue-50 text-blue-700" : "bg-emerald-50 text-emerald-700"}>
                            {programType === "PGP_TBM" ? "PGP TBM" : "PGP YLC"}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium text-gray-900">{student.name || "N/A"}</p>
                            <p className="text-sm text-gray-500">{student.email}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Briefcase className="h-4 w-4 text-gray-400" />
                            <span className="text-gray-600">{student.current_organization || "Not specified"}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className="text-sm text-gray-500" data-testid={`date-${student.id}`}>{formatDate(student.created_at)}</span>
                        </TableCell>
                        <TableCell>
                          <Badge className={isComplete ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"} data-testid={`status-${student.id}`}
                            title={isComplete ? `Generated ${student.report_generated_at ? new Date(student.report_generated_at).toLocaleString() : ""}` : (student.report_error || "Report is a placeholder. Regenerate it.")}>
                            {isComplete ? <><CheckCircle className="h-3 w-3 mr-1" />Complete</> : <><AlertCircle className="h-3 w-3 mr-1" />Incomplete</>}
                          </Badge>
                          {!isComplete && student.report_error && (
                            <p className="text-[11px] text-amber-700 mt-1 max-w-[220px] truncate" title={student.report_error}>{student.report_error}</p>
                          )}
                        </TableCell>
                        <TableCell>
                          {student.resume_link ? (
                            <a href={student.resume_link} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-blue-600 hover:text-blue-700 text-sm" onClick={(e) => e.stopPropagation()}>
                              <FileText className="h-4 w-4" /><span>View</span><ExternalLink className="h-3 w-3" />
                            </a>
                          ) : <span className="text-gray-400 text-sm">No resume</span>}
                        </TableCell>
                        <TableCell>
                          <Button variant="ghost" size="sm"
                            className={`h-8 px-3 gap-2 ${copiedId === student.id ? "text-emerald-600 bg-emerald-50" : "text-gray-500 hover:text-[#B8860B] hover:bg-[#E8B923]/10"}`}
                            onClick={(e) => { e.stopPropagation(); handleShareLink(student); }}
                            disabled={generatingLink === student.id}
                            data-testid={`share-link-btn-${student.id}`}
                          >
                            {generatingLink === student.id ? <Loader2 className="h-4 w-4 animate-spin" />
                              : copiedId === student.id ? <><Check className="h-4 w-4" /><span className="text-xs">Copied!</span></>
                              : <><Link className="h-4 w-4" /><span className="text-xs">Copy Link</span></>}
                          </Button>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button variant="ghost" size="icon" className="h-8 w-8 text-gray-400 hover:text-gray-700 hover:bg-gray-100" onClick={(e) => { e.stopPropagation(); openEditModal(student); }} data-testid={`edit-btn-${student.id}`}>
                              <Edit2 className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="icon" className="h-8 w-8 text-gray-400 hover:text-red-600 hover:bg-red-50" onClick={(e) => { e.stopPropagation(); openDeleteDialog(student); }} data-testid={`delete-btn-${student.id}`}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                            <Button size="sm" className="bg-[#E8B923] text-black hover:bg-[#D4A017]" onClick={(e) => { e.stopPropagation(); viewReport(student.id); }} data-testid={`view-report-btn-${student.id}`}>
                              View Report<ChevronRight className="h-4 w-4 ml-1" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Bottom Actions: Regenerate All / Incomplete */}
      {!regenerating && total > 0 && (
        <div className="flex items-center justify-end gap-3">
          <Button onClick={handleRecalcWorkExp} variant="outline" className="border-gray-300 text-gray-600 hover:bg-gray-50" data-testid="recalc-work-exp-btn">
            <RefreshCw className="h-4 w-4 mr-2" /> Fix Work Experience
          </Button>
          <Button onClick={handleRefreshReports} variant="outline" className="border-gray-300 text-gray-600 hover:bg-gray-50" title="Rebuilds the Academic Roadmap, Prep Roadmap and Mentors tabs of every report from the latest uploads. Does not use AI." data-testid="refresh-reports-btn">
            <RefreshCw className="h-4 w-4 mr-2" /> Refresh Roadmaps (no AI)
          </Button>
          <Button onClick={handleFillPrepGaps} variant="outline" className="border-gray-300 text-gray-600 hover:bg-gray-50" title="Fills empty Prep Roadmap cells (books, case practice, tasks, skills) for every domain with Claude-written suggestions. Existing content is kept." data-testid="fill-prep-gaps-btn">
            <RefreshCw className="h-4 w-4 mr-2" /> Fill Prep Gaps (AI)
          </Button>
          <Button onClick={handleRegenerateIncomplete} variant="outline" className="border-[#E8B923] text-[#B8941C] hover:bg-[#FFF8E1]" title="Runs the AI again only for students whose report is missing or a placeholder" data-testid="regenerate-incomplete-btn">
            <RefreshCw className="h-4 w-4 mr-2" /> Regenerate Incomplete
          </Button>
          <Button onClick={() => { if (window.confirm(`Regenerate all ${total} reports with AI? This uses API credits and takes a while.`)) handleRegenerateAll(); }} className="bg-[#E8B923] text-black hover:bg-[#D4A017]" title="Runs the AI again for every student (costs API credits)" data-testid="regenerate-all-btn">
            <RefreshCw className="h-4 w-4 mr-2" /> Regenerate All
          </Button>
        </div>
      )}

      {/* Edit Student Modal */}
      <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
        <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-lg">
          <DialogHeader><DialogTitle className="text-xl font-bold">Edit Student</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label className="text-gray-600">Name</Label><Input value={editForm.name} onChange={(e) => handleEditFormChange("name", e.target.value)} className="bg-white border-gray-200 text-gray-900" /></div>
              <div className="space-y-2"><Label className="text-gray-600">Email</Label><Input value={editForm.email} onChange={(e) => handleEditFormChange("email", e.target.value)} className="bg-white border-gray-200 text-gray-900" /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label className="text-gray-600">Mobile</Label><Input value={editForm.mobile} onChange={(e) => handleEditFormChange("mobile", e.target.value)} className="bg-white border-gray-200 text-gray-900" /></div>
              <div className="space-y-2"><Label className="text-gray-600">Program Type</Label>
                <Select value={editForm.program_type} onValueChange={(value) => handleEditFormChange("program_type", value)}>
                  <SelectTrigger className="bg-white border-gray-200 text-gray-900"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white border-gray-200"><SelectItem value="PGP_TBM">PGP TBM</SelectItem><SelectItem value="PGP_YLC">PGP YLC</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label className="text-gray-600">Organization</Label><Input value={editForm.current_organization} onChange={(e) => handleEditFormChange("current_organization", e.target.value)} className="bg-white border-gray-200 text-gray-900" /></div>
              <div className="space-y-2"><Label className="text-gray-600">Designation</Label><Input value={editForm.current_designation} onChange={(e) => handleEditFormChange("current_designation", e.target.value)} className="bg-white border-gray-200 text-gray-900" /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label className="text-gray-600">Work Experience (years)</Label><Input type="number" step="0.1" value={editForm.total_work_experience} onChange={(e) => handleEditFormChange("total_work_experience", parseFloat(e.target.value) || 0)} className="bg-white border-gray-200 text-gray-900" /></div>
              <div className="space-y-2"><Label className="text-gray-600">LinkedIn URL</Label><Input value={editForm.linkedin} onChange={(e) => handleEditFormChange("linkedin", e.target.value)} className="bg-white border-gray-200 text-gray-900" placeholder="https://linkedin.com/in/..." /></div>
            </div>
            <div className="space-y-2"><Label className="text-gray-600">Resume Link</Label><Input value={editForm.resume_link} onChange={(e) => handleEditFormChange("resume_link", e.target.value)} className="bg-white border-gray-200 text-gray-900" placeholder="https://..." /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditModalOpen(false)} className="border-gray-200 text-gray-600">Cancel</Button>
            <Button onClick={handleSaveEdit} disabled={saving} className="bg-[#E8B923] text-black hover:bg-[#D4A017]">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-white border-gray-200">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-gray-900">Delete Student</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-500">
              Are you sure you want to delete <span className="text-gray-900 font-medium">{studentToDelete?.name}</span>? This will also delete their career report.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-gray-200 text-gray-600 hover:bg-gray-50">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} disabled={deleting} className="bg-red-600 text-white hover:bg-red-700">
              {deleting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Delete Dialog */}
      <AlertDialog open={bulkDeleteDialogOpen} onOpenChange={setBulkDeleteDialogOpen}>
        <AlertDialogContent className="bg-white border-gray-200">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-gray-900">Delete {selectedStudents.size} Students?</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-500">
              This will permanently delete {selectedStudents.size} selected students and their reports.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-gray-200 text-gray-600 hover:bg-gray-50">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleBulkDelete} disabled={bulkDeleting} className="bg-red-600 text-white hover:bg-red-700" data-testid="confirm-bulk-delete-btn">
              {bulkDeleting ? "Deleting..." : `Delete ${selectedStudents.size} Students`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
